// src/pages/reports/RewardsPointsBook/RewardsTrFilterPopup.tsx
import React, { Dispatch, SetStateAction, forwardRef, useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Checkbox,
  CircularProgress,
  InputAdornment,
  GlobalStyles,
} from "@mui/material";
// ⛔️ Autocomplete not needed anymore
// import Autocomplete from "@mui/material/Autocomplete";
// import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
// import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getalllocations, getalloutlettype } from "../../assign-task/api";
import { getTransactionFilterOptions } from "../api";
import { getToken } from "../../../utils/utils";

import arrowLeft from "../../../assests/images/arrow-left.svg";
import arrowRight from "../../../assests/images/arrow-right.png";
import doubleArrowLeft from "../../../assests/images/double-arrow-left.svg";
import doubleArrowRight from "../../../assests/images/double-arrow-right.svg";

// const ICON = <CheckBoxOutlineBlankIcon fontSize="small" />;
// const CHECKED_ICON = <CheckBoxIcon fontSize="small" />;
const SELECT_ALL_LABEL = "Select All";

const CLEAR_X_DATA_URI =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="#888" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59L7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.9a1 1 0 0 0 1.42-1.42L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4Z"/></svg>`
  );

const CustomArrow = ({ open, onClick }: { open: boolean; onClick: () => void }) => (
  <span
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    style={{
      display: "inline-block",
      width: 10,
      height: 10,
      borderRight: "2px solid #222",
      borderBottom: "2px solid #222",
      transform: open ? "rotate(-135deg)" : "rotate(45deg)",
      transformOrigin: "center center",
      transition: "transform 0.18s ease",
      marginLeft: 8,
      position: "relative",
      cursor: "pointer",
      boxSizing: "border-box",
      WebkitTransform: open ? "rotate(-135deg)" : "rotate(45deg)",
      WebkitTransformOrigin: "center center",
    }}
  />
);

/** ✅ Searchable multi-select used across all pickers */
function SearchableMultiSelect({
  label,
  options,
  value,
  onChange,
  loading,
  formatOptionLabel,
}: {
  label: string;
  options: string[];
  value: string[];
  onChange: (vals: string[]) => void;
  loading?: boolean;
  /** display-only formatter; raw values preserved for backend */
  formatOptionLabel?: (s: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const fmt = (s: string) => (formatOptionLabel ? formatOptionLabel(s) : s);

  // close on outside click / ESC
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearchValue("");
      }
    };
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && (setOpen(false), setSearchValue(""));
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const filtered = (options || []).filter((opt) => fmt(opt).toLowerCase().includes(searchValue.toLowerCase()));
  const allSelected = value.length > 0 && value.length === options.length;

  const displayedValue =
    value.length === 0 ? label : value.length <= 2 ? value.map(fmt).join(", ") : `${value.slice(0, 2).map(fmt).join(", ")}…`;

  const toggleOption = (opt: string) =>
    value.includes(opt) ? onChange(value.filter((v) => v !== opt)) : onChange([...value, opt]);

  const toggleSelectAll = () => (allSelected ? onChange([]) : onChange(options.slice()));

  return (
    <Box ref={containerRef} sx={{ width: "100%", position: "relative", userSelect: "none" }}>
      <TextField
        size="small"
        variant="outlined"
        value={displayedValue}
        onClick={() => setOpen((v) => !v)}
        placeholder={label}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <>
              {loading ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
              <InputAdornment position="end" sx={{ cursor: "pointer" }}>
                <CustomArrow open={open} onClick={() => setOpen((v) => !v)} />
              </InputAdornment>
            </>
          ),
          sx: {
            cursor: "pointer",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
        fullWidth
      />

      {open && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            maxHeight: 250,
            overflowY: "auto",
            border: "1px solid #ccc",
            backgroundColor: "#fff",
            zIndex: 3500,
            mt: 0.5,
            borderRadius: 1,
            boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box sx={{ px: 1, py: 0.5 }}>
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search..."
              style={{
                width: "100%",
                padding: "10px 10px",
                borderRadius: 4,
                border: "1px solid #ddd",
                fontSize: "0.9rem",
                marginBottom: 8,
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
                marginTop: "5px",
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </Box>

          <Box sx={{ px: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1, cursor: "pointer" }} onClick={toggleSelectAll}>
              <Checkbox checked={allSelected} indeterminate={value.length > 0 && !allSelected} />
              <Typography variant="body2">{SELECT_ALL_LABEL}</Typography>
            </Box>

            {filtered.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1 }}>
                No options found
              </Typography>
            )}

            {filtered.map((opt) => {
              const checked = value.includes(opt);
              return (
                <Box
                  key={opt}
                  sx={{ display: "flex", alignItems: "center", py: 0.5, cursor: "pointer" }}
                  onClick={() => toggleOption(opt)}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <Checkbox checked={checked} />
                  <Typography variant="body2">{fmt(opt)}</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export interface FilterPayload {
  locations: string[];
  outletTypes: string[];
  outletNames?: string[] | null;
  outlet_name?: string[] | null;
  transactionTypes?: string[] | null;
  redemptionStatuses?: string[] | null;
  lastloginStart: string | null;
  lastloginEnd: string | null;
  memberSinceStart: string | null;
  memberSinceEnd: string | null;
  backendFilters?: {
    city?: string[] | null;
    outlet_category?: string[] | null;
    outlet_name?: string[] | null;
    transaction_type?: string[] | null;
    redemption_status?: string[] | null;
    fromdate?: string | null;
    todate?: string | null;
    memberSinceStart?: string | null;
    memberSinceEnd?: string | null;
  } | null;
}

interface BackendFilterOptions {
  cities?: string[];
  outlets?: string[];
  cityToOutlets?: Record<string, string[]>;
  regions?: string[];
  outletCategories?: string[];
  transactionTypesByOutlet?: Record<string, string[]>;
  redemptionStatusByOutlet?: Record<string, string[]>;
  outletPairs?: Array<{ label: string; value: string }>;
}

interface Props {
  onApplyFilters: (payload: FilterPayload) => void;
  dateRange: [Date | null, Date | null];
  setDateRange: Dispatch<SetStateAction<[Date | null, Date | null]>>;
  initialFilters?: Partial<FilterPayload> | null;
  selectedTab?: number;
  setSelectedTab?: Dispatch<SetStateAction<number>>;
  selectedDate?: Date | null;
  setSelectedDate?: Dispatch<SetStateAction<Date | null>>;
  selectedlastlogin?: string[];
  setSelectedlastlogin?: Dispatch<SetStateAction<string[]>>;
  backendFilterOptions?: BackendFilterOptions | null;

  isOpen?: boolean;
  shouldFetch?: boolean;
  onFetched?: () => void;
  onCancel?: () => void;
}

const ForwardedTextInput = forwardRef<HTMLInputElement, any>(
  ({ value, onClick, placeholder, style, sx, ...rest }, ref) => {
    const mergedStyle = { paddingRight: 48, ...(style || {}), width: "100%" };
    return (
      <TextField
        size="small"
        variant="outlined"
        inputRef={ref}
        onClick={onClick}
        value={value}
        placeholder={placeholder || "Select date range"}
        fullWidth
        {...rest}
        sx={{ position: "relative", width: "100%", ...(sx || {}) }}
        InputProps={{
          ...(rest.InputProps || {}),
          endAdornment: (
            <>
              {rest.InputProps?.endAdornment}
              <InputAdornment position="end" sx={{ position: "absolute", right: 8, pointerEvents: "auto" }}>
                <CustomArrow
                  open={false}
                  onClick={() => {
                    try {
                      onClick && (onClick as unknown as () => void)();
                    } catch {
                      try {
                        onClick?.();
                      } catch {}
                    }
                  }}
                />
              </InputAdornment>
            </>
          ),
        }}
        inputProps={{ ...(rest.inputProps || {}), style: mergedStyle, readOnly: true }}
      />
    );
  }
);
ForwardedTextInput.displayName = "ForwardedTextInput";

export default function RewardsTrFilterPopup({
  onApplyFilters,
  dateRange,
  setDateRange,
  initialFilters,
  selectedTab,
  setSelectedTab,
  selectedDate,
  setSelectedDate,
  selectedlastlogin,
  setSelectedlastlogin,
  backendFilterOptions = null,
  isOpen = false,
  shouldFetch = false,
  onFetched,
  onCancel,
}: Props) {
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `.MuiAutocomplete-popper{ z-index: 3000 !important; }`;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // options
  const [locationOptions, setLocationOptions] = useState<string[]>([]);
  const [outletTypeOptions, setOutletTypeOptions] = useState<string[]>([]);
  const [outletNameOptions, setOutletNameOptions] = useState<string[]>([]);

  const [transactionTypeOptionsState] = useState<string[]>([
    "Redeemed",
    "Rewarded",
    "Pending Redemption",
    "Rejected",
    "Other",
    "No Activity",
  ]);
  const [redemptionStatusOptionsState] = useState<string[]>([
    "Pending",
    "Delivered",
    "Not applied for redemption",
    "Undefined",
    "Expired",
    "Rejected",
    "Approved",
  ]);

  // helper to read arrays from different response shapes
const safeArray = (val: any): string[] =>
  Array.isArray(val?.data?.data)
    ? val.data.data
    : Array.isArray(val?.data)
    ? val.data
    : Array.isArray(val)
    ? val
    : [];

  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingOutletTypes, setLoadingOutletTypes] = useState(false);
  const [loadingOutletNames, setLoadingOutletNames] = useState(false);

  // selections
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedOutletTypes, setSelectedOutletTypes] = useState<string[]>([]);
  const [selectedOutletNames, setSelectedOutletNames] = useState<string[]>([]);
  const [selectedTransactionTypes, setSelectedTransactionTypes] = useState<string[]>([]);
  const [selectedRedemptionStatuses, setSelectedRedemptionStatuses] = useState<string[]>([]);

  const [memberSinceRange, setMemberSinceRange] = useState<[Date | null, Date | null]>([null, null]);
  const [localDateRange, setLocalDateRange] = useState<[Date | null, Date | null]>(dateRange);

  // derived lists
  const [derivedOutletNameOptions, setDerivedOutletNameOptions] = useState<string[]>([]);
  const [derivedTransactionTypeOptions, setDerivedTransactionTypeOptions] = useState<string[]>(transactionTypeOptionsState);
  const [derivedRedemptionStatusOptions, setDerivedRedemptionStatusOptions] = useState<string[]>(redemptionStatusOptionsState);

  // normalization maps
  const [normOutletsByCity, setNormOutletsByCity] = useState<Record<string, string[]>>({});
  const [normTxByOutlet, setNormTxByOutlet] = useState<Record<string, string[]>>({});
  const [normRxByOutlet, setNormRxByOutlet] = useState<Record<string, string[]>>({});
  const [normOutletToCanonical, setNormOutletToCanonical] = useState<Record<string, string[]>>({});

  const normalize = (s?: string | null) => (s ? s.toString().trim().toLowerCase() : "");

  // lifecycle guards
  const didRehydrateRef = useRef(false);
  const didFetchRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const outletsReady = outletNameOptions.length > 0 || Object.keys(normOutletsByCity).length > 0;

  // fetch
  useEffect(() => {
  if (!isOpen) return;
  if (!shouldFetch) return;
  if (didFetchRef.current) return;

  let mounted = true;
  didFetchRef.current = true;

  const fetchLists = async () => {
    try {
      const token = getToken();

      // A) Pull master lists for Locations & Outlet Types from their dedicated APIs
      setLoadingLocations(true);
      setLoadingOutletTypes(true);

      const [locRes, typeRes] = await Promise.allSettled([
        getalllocations(),
        getalloutlettype(),
      ]);

      if (!mounted || !mountedRef.current) return;

      if (locRes.status === "fulfilled") {
        const locs = safeArray(locRes.value).map((s) => String(s).trim()).filter(Boolean);
        setLocationOptions(Array.from(new Set(locs)).sort());
      } else {
        console.warn("getalllocations failed:", (locRes as any).reason);
        setLocationOptions([]);
      }

      if (typeRes.status === "fulfilled") {
        const types = safeArray(typeRes.value).map((s) => String(s).trim()).filter(Boolean);
        setOutletTypeOptions(Array.from(new Set(types)).sort());
      } else {
        console.warn("getalloutlettype failed:", (typeRes as any).reason);
        setOutletTypeOptions([]);
      }

      // B) Still fetch the transaction filter options (outlets + maps only)
      setLoadingOutletNames(true);

      if (typeof getTransactionFilterOptions === "function") {
        const txOpts = await getTransactionFilterOptions(
          selectedLocations.length ? selectedLocations : null,
          token,
          undefined,
          { outletNames: null, pmiEntityCodes: null }
        );
        if (!mounted || !mountedRef.current) return;

        // Outlets list (global)
        const outlets = Array.isArray(txOpts?.outlets) ? txOpts.outlets : [];
        setOutletNameOptions(
          Array.from(new Set(outlets.map((o: any) => String(o).trim()))).sort()
        );

        // City -> Outlets map
        if (txOpts?.cityToOutlets && Object.keys(txOpts.cityToOutlets).length > 0) {
          const map: Record<string, string[]> = {};
          Object.keys(txOpts.cityToOutlets).forEach((city) => {
            const norm = normalize(city);
            map[norm] = (txOpts.cityToOutlets[city] || []).map((o: any) => String(o));
          });
          setNormOutletsByCity(map);
        } else {
          setNormOutletsByCity({});
        }

        // Tx / Rx maps per outlet
        if (txOpts?.transactionTypesByOutlet && Object.keys(txOpts.transactionTypesByOutlet).length > 0) {
          const obj: Record<string, string[]> = {};
          Object.keys(txOpts.transactionTypesByOutlet).forEach((k) => {
            obj[k.toString().trim().toLowerCase()] = Array.isArray(txOpts.transactionTypesByOutlet[k])
              ? txOpts.transactionTypesByOutlet[k].map(String)
              : [];
          });
          setNormTxByOutlet(obj);
        } else {
          setNormTxByOutlet({});
        }

        if (txOpts?.redemptionStatusByOutlet && Object.keys(txOpts.redemptionStatusByOutlet).length > 0) {
          const obj: Record<string, string[]> = {};
          Object.keys(txOpts.redemptionStatusByOutlet).forEach((k) => {
            obj[k.toString().trim().toLowerCase()] = Array.isArray(txOpts.redemptionStatusByOutlet[k])
              ? txOpts.redemptionStatusByOutlet[k].map(String)
              : [];
          });
          setNormRxByOutlet(obj);
        } else {
          setNormRxByOutlet({});
        }

        // Canonicalization for outlet variations
        const newNormOutletToCanonical: Record<string, string[]> = {};
        outlets.forEach((o: any) => {
          const no = normalize(o);
          if (!newNormOutletToCanonical[no]) newNormOutletToCanonical[no] = [o];
          else if (!newNormOutletToCanonical[no].includes(o)) newNormOutletToCanonical[no].push(o);
        });
        setNormOutletToCanonical(newNormOutletToCanonical);
      }

      try { onFetched?.(); } catch {}
    } catch (err) {
      console.warn("filter-options load failed:", err);
    } finally {
      if (!mounted || !mountedRef.current) return;
      setLoadingLocations(false);
      setLoadingOutletTypes(false);
      setLoadingOutletNames(false);
    }
  };

  fetchLists();

  return () => {
    mounted = false;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [isOpen, shouldFetch]);


  // rehydrate from initialFilters
  useEffect(() => {
    if (!isOpen) return;
    if (!initialFilters) return;
    if (didRehydrateRef.current) return;

    didRehydrateRef.current = true;

    if (initialFilters.locations) setSelectedLocations(initialFilters.locations);
    if (initialFilters.outletTypes) setSelectedOutletTypes(initialFilters.outletTypes);
    if (initialFilters.outletNames) setSelectedOutletNames(initialFilters.outletNames ?? []);
    if (initialFilters.transactionTypes) setSelectedTransactionTypes(initialFilters.transactionTypes ?? []);
    if (initialFilters.redemptionStatuses) setSelectedRedemptionStatuses(initialFilters.redemptionStatuses ?? []);

    const backendFrom = (initialFilters as any)?.backendFilters?.fromdate ?? null;
    const backendTo = (initialFilters as any)?.backendFilters?.todate ?? null;
    const txnStart = backendFrom ? new Date(backendFrom) : initialFilters.lastloginStart ? new Date(initialFilters.lastloginStart) : null;
    const txnEnd = backendTo ? new Date(backendTo) : initialFilters.lastloginEnd ? new Date(initialFilters.lastloginEnd) : null;

    setLocalDateRange([txnStart, txnEnd]); // ✅ local only (do NOT push to parent here)

    const backendMemStart = (initialFilters as any)?.backendFilters?.memberSinceStart ?? null;
    const backendMemEnd = (initialFilters as any)?.backendFilters?.memberSinceEnd ?? null;
    const memStart = backendMemStart ? new Date(backendMemStart) : initialFilters.memberSinceStart ? new Date(initialFilters.memberSinceStart) : null;
    const memEnd = backendMemEnd ? new Date(backendMemEnd) : initialFilters.memberSinceEnd ? new Date(initialFilters.memberSinceEnd) : null;
    setMemberSinceRange([memStart, memEnd]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialFilters]);

  useEffect(() => setLocalDateRange(dateRange), [dateRange]);

  // Build normalized maps from backendFilterOptions
  useEffect(() => {
    if (!backendFilterOptions) {
      setNormOutletsByCity({});
      return;
    }

    const obc = backendFilterOptions.cityToOutlets || {};
    const globalOutlets = backendFilterOptions.outlets || [];
    const backendCities = backendFilterOptions.cities || [];

    const newNormOutletsByCity: Record<string, string[]> = {};
    const newNormOutletToCanonical: Record<string, string[]> = {};

    const normalize = (s?: string | null) => (s ? s.toString().trim().toLowerCase() : "");

    Object.keys(obc).forEach((cityKey) => {
      const normCity = normalize(cityKey);
      const outlets = Array.isArray(obc[cityKey]) ? obc[cityKey] : [];
      newNormOutletsByCity[normCity] = Array.from(new Set(outlets.map((o) => (o || "").toString().trim())));
      outlets.forEach((o: string) => {
        const no = normalize(o);
        if (!newNormOutletToCanonical[no]) newNormOutletToCanonical[no] = [];
        if (!newNormOutletToCanonical[no].includes(o)) newNormOutletToCanonical[no].push(o);
      });
    });

    globalOutlets.forEach((o) => {
      const no = normalize(o);
      if (!newNormOutletToCanonical[no]) newNormOutletToCanonical[no] = [o];
      else if (!newNormOutletToCanonical[no].includes(o)) newNormOutletToCanonical[no].push(o);
    });

    setNormOutletsByCity(newNormOutletsByCity);
    setNormOutletToCanonical((prev) => ({ ...prev, ...newNormOutletToCanonical }));

    if (Array.isArray(backendCities) && backendCities.length > 0) {
      setLocationOptions(Array.from(new Set(backendCities.map((c) => String(c).trim()))).sort());
    }
    if (Array.isArray(globalOutlets) && globalOutlets.length > 0) {
      setOutletNameOptions(Array.from(new Set(globalOutlets.map((o) => String(o).trim()))).sort());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendFilterOptions]);

  // === Derived outlet names based on selectedLocations
  useEffect(() => {
    if (!outletsReady) return;

    const collected: string[] = [];

    if (selectedLocations.length === 0) {
      const global = outletNameOptions.slice().sort();
      setDerivedOutletNameOptions(global);
      if (global.length > 0) setSelectedOutletNames((prev) => prev.filter((x) => global.includes(x)));
      return;
    }

    selectedLocations.forEach((city) => {
      const normCity = (city || "").toString().trim().toLowerCase();
      const arr = normOutletsByCity[normCity] || [];
      collected.push(...arr);
      if (arr.length === 0) {
        const fallbackKeys = Object.keys(normOutletsByCity).filter((k) => k.includes(normCity) || normCity.includes(k));
        fallbackKeys.forEach((k) => collected.push(...(normOutletsByCity[k] || [])));
      }
    });

    const unique = Array.from(new Set(collected)).sort();
    const fallback = outletNameOptions.slice().sort();
    const finalList = unique.length ? unique : fallback;

    setDerivedOutletNameOptions(finalList);
    if (finalList.length > 0) {
      setSelectedOutletNames((prev) => prev.filter((x) => finalList.includes(x)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocations, normOutletsByCity, outletNameOptions, outletsReady]);

  // Derived tx/rx when outlets/locations change
  useEffect(() => {
    let outletsToConsiderNorm: string[] = [];

    const normalize = (s?: string | null) => (s ? s.toString().trim().toLowerCase() : "");

    if (selectedOutletNames.length > 0) {
      outletsToConsiderNorm = selectedOutletNames.map((o) => normalize(o));
    } else if (selectedLocations.length > 0) {
      const collected: string[] = [];
      selectedLocations.forEach((city) => {
        const normCity = normalize(city);
        const arr = normOutletsByCity[normCity] || [];
        collected.push(...arr);
        if ((arr || []).length === 0) {
          const fallbackKeys = Object.keys(normOutletsByCity).filter((k) => k.includes(normCity) || normCity.includes(k));
          fallbackKeys.forEach((k) => collected.push(...(normOutletsByCity[k] || [])));
        }
      });
      outletsToConsiderNorm = Array.from(new Set(collected)).map((o) => normalize(o));
    } else {
      outletsToConsiderNorm = outletNameOptions.map((o) => normalize(o));
    }

    const txCollected: string[] = [];
    const rxCollected: string[] = [];

    outletsToConsiderNorm.forEach((normOutlet) => {
      const tx = normTxByOutlet[normOutlet] || [];
      const rx = normRxByOutlet[normOutlet] || [];
      txCollected.push(...tx);
      rxCollected.push(...rx);
    });

    const txUnique = Array.from(new Set(txCollected.length ? txCollected : transactionTypeOptionsState)).sort();
    const rxUnique = Array.from(new Set(rxCollected.length ? rxCollected : redemptionStatusOptionsState)).sort();

    setDerivedTransactionTypeOptions(txUnique);
    setDerivedRedemptionStatusOptions(rxUnique);

    setSelectedTransactionTypes((prev) => prev.filter((x) => txUnique.includes(x)));
    setSelectedRedemptionStatuses((prev) => prev.filter((x) => rxUnique.includes(x)));
  }, [
    selectedOutletNames,
    selectedLocations,
    normTxByOutlet,
    normRxByOutlet,
    normOutletsByCity,
    outletNameOptions,
    transactionTypeOptionsState,
    redemptionStatusOptionsState,
  ]);

  const formatDateForPayload = (d: Date | null): string | null => {
    if (!d) return null;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const clearAll = () => {
    setSelectedLocations([]);
    setSelectedOutletTypes([]);
    setSelectedOutletNames([]);
    setSelectedTransactionTypes([]);
    setSelectedRedemptionStatuses([]);
    setLocalDateRange([null, null]);
    setMemberSinceRange([null, null]);
    setDateRange([null, null]); // keep clearing the parent on CLEAR ALL

    if (typeof setSelectedTab === "function") {
      try { setSelectedTab(0); } catch {}
    }
    if (typeof setSelectedDate === "function") {
      try { setSelectedDate(null); } catch {}
    }
    if (typeof setSelectedlastlogin === "function") {
      try { setSelectedlastlogin([]); } catch {}
    }

    onApplyFilters({
      locations: [],
      outletTypes: [],
      outletNames: null,
      outlet_name: null,
      transactionTypes: null,
      redemptionStatuses: null,
      lastloginStart: null,
      lastloginEnd: null,
      memberSinceStart: null,
      memberSinceEnd: null,
      backendFilters: {
        city: null,
        outlet_category: null,
        outlet_name: null,
        transaction_type: null,
        redemption_status: null,
        fromdate: null,
        todate: null,
        memberSinceStart: null,
        memberSinceEnd: null,
      },
    });
  };

  const handleApply = () => {
    const [lastStart, lastEnd] = localDateRange;
    const [memberStart, memberEnd] = memberSinceRange;

    const payload: any = {
      locations: selectedLocations.slice(),
      outletTypes: selectedOutletTypes.slice(),
      outletNames: selectedOutletNames.length > 0 ? selectedOutletNames.slice() : null,
      outlet_name: selectedOutletNames.length > 0 ? selectedOutletNames.slice() : null,
      transactionTypes: selectedTransactionTypes.length > 0 ? selectedTransactionTypes.slice() : null,
      redemptionStatuses: selectedRedemptionStatuses.length > 0 ? selectedRedemptionStatuses.slice() : null,
      lastloginStart: formatDateForPayload(lastStart),
      lastloginEnd: formatDateForPayload(lastEnd),
      memberSinceStart: formatDateForPayload(memberStart),
      memberSinceEnd: formatDateForPayload(memberEnd),
      backendFilters: {
        city: selectedLocations.length > 0 ? selectedLocations.slice() : null,
        outlet_category: selectedOutletTypes.length > 0 ? selectedOutletTypes.slice() : null,
        outlet_name: selectedOutletNames.length > 0 ? selectedOutletNames.slice() : null,
        transaction_type: selectedTransactionTypes.length > 0 ? selectedTransactionTypes.slice() : null,
        redemption_status: selectedRedemptionStatuses.length > 0 ? selectedRedemptionStatuses.slice() : null,
        fromdate: formatDateForPayload(localDateRange[0]),
        todate: formatDateForPayload(localDateRange[1]),
        memberSinceStart: formatDateForPayload(memberStart),
        memberSinceEnd: formatDateForPayload(memberEnd),
      },
    };

    // ✅ push the chosen range to the header (parent) only on APPLY
    setDateRange([lastStart ?? null, lastEnd ?? null]);

    onApplyFilters(payload);
  };

  const formatRangeText = (range: [Date | null, Date | null]) => {
    if (!range || (!range[0] && !range[1])) return "Select date range";
    const s = range[0] ? range[0].toLocaleDateString() : "";
    const e = range[1] ? range[1].toLocaleDateString() : "";
    return s && e ? `${s} - ${e}` : s || e || "Select date range";
  };

  const btnResetStyle: React.CSSProperties = {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const renderHeaderWithIcons = ({
    date,
    decreaseMonth,
    increaseMonth,
    decreaseYear,
    increaseYear,
  }: {
    date: Date;
    decreaseMonth: () => void;
    increaseMonth: () => void;
    decreaseYear: () => void;
    increaseYear: () => void;
  }) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <button type="button" onClick={decreaseYear} aria-label="Previous year" style={btnResetStyle}>
        <img src={doubleArrowLeft} alt="Previous year" />
      </button>
      <button type="button" onClick={decreaseMonth} aria-label="Previous month" style={btnResetStyle}>
        <img src={arrowLeft} alt="Previous month" />
      </button>

      <span style={{ margin: "0 10px", fontWeight: 600 }}>
        {date.toLocaleString("default", { year: "numeric", month: "long" })}
      </span>

      <button type="button" onClick={increaseMonth} aria-label="Next month" style={btnResetStyle}>
        <img src={arrowRight} alt="Next month" />
      </button>
      <button type="button" onClick={increaseYear} aria-label="Next year" style={btnResetStyle}>
        <img src={doubleArrowRight} alt="Next year" />
      </button>
    </div>
  );

  return (
    <Box sx={{ width: 420, maxHeight: "80vh", background: "#fff", borderRadius: 2, p: 0, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", display: "flex", flexDirection: "column" }}>
      <Box sx={{ p: 2.5, borderBottom: "1px solid #eee" }}>
        <Typography sx={{ fontWeight: 600 }}>Apply Filter</Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Locations</Typography>
          <SearchableMultiSelect
            label="Select Locations"
            options={locationOptions}
            value={selectedLocations}
            onChange={setSelectedLocations}
            loading={loadingLocations}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Outlet Types</Typography>
          <SearchableMultiSelect
            label="Select Outlet Types"
            options={outletTypeOptions}
            value={selectedOutletTypes}
            onChange={setSelectedOutletTypes}
            loading={loadingOutletTypes}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Outlet Names</Typography>
          <SearchableMultiSelect
            label="Select Outlet Names"
            options={derivedOutletNameOptions.length ? derivedOutletNameOptions : outletNameOptions}
            value={selectedOutletNames}
            onChange={setSelectedOutletNames}
            loading={loadingOutletNames}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Transaction Type</Typography>
          <SearchableMultiSelect
            label="Transaction Type"
            options={derivedTransactionTypeOptions}
            value={selectedTransactionTypes}
            onChange={setSelectedTransactionTypes}
            loading={false}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Redemption Status</Typography>
          <SearchableMultiSelect
            label="Redemption Status"
            options={derivedRedemptionStatusOptions}
            value={selectedRedemptionStatuses}
            onChange={setSelectedRedemptionStatuses}
            loading={false}
          />
        </Box>

        {/* TRANSACTION DATE */}
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Transaction Date</Typography>
          <Box sx={{ width: "100%" }}>
            <GlobalStyles
              styles={{
                ".react-datepicker-wrapper": { width: "100% !important" },
                // keep any legacy Autocomplete overrides harmlessly
                '.MuiAutocomplete-option, .MuiAutocomplete-option[aria-selected="true"], .MuiAutocomplete-option.Mui-focused, .MuiAutocomplete-option:hover, li[role="option"], li[role="option"][aria-selected="true"], li[role="option"].Mui-focusVisible': {
                  background: "transparent !important",
                },
                ".MuiTouchRipple-root": { display: "none !important" },
              }}
            />
            <DatePicker
              selectsRange
              startDate={localDateRange[0] ?? undefined}
              endDate={localDateRange[1] ?? undefined}
              onChange={(range: [Date | null, Date | null]) => {
                // keep local only; parent updates on APPLY
                setLocalDateRange(range);
              }}
              dateFormat="yyyy-MM-dd"
              renderCustomHeader={renderHeaderWithIcons}
              customInput={
                <ForwardedTextInput
                  placeholder={formatRangeText(localDateRange)}
                  key={`txn-${localDateRange[0]?.getTime() ?? 0}-${localDateRange[1]?.getTime() ?? 0}`}
                  sx={{ width: "100%" }}
                  InputProps={{
                    endAdornment:
                      localDateRange[0] || localDateRange[1] ? (
                        <img
                          onClick={(e) => {
                            e.stopPropagation();
                            // clear local only; parent updates on APPLY/CLEAR ALL
                            setLocalDateRange([null, null]);
                          }}
                          src={CLEAR_X_DATA_URI}
                          alt="Clear"
                          style={{ height: 18, width: 18, cursor: "pointer", marginRight: 10, paddingTop: "4px" }}
                        />
                      ) : undefined,
                  }}
                />
              }
            />
          </Box>
        </Box>

        {/* MEMBER SINCE */}
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Member Since</Typography>
          <Box sx={{ width: "100%" }}>
            <DatePicker
              selectsRange
              startDate={memberSinceRange[0] ?? undefined}
              endDate={memberSinceRange[1] ?? undefined}
              onChange={(range: [Date | null, Date | null]) => setMemberSinceRange(range)}
              dateFormat="yyyy-MM-dd"
              renderCustomHeader={renderHeaderWithIcons}
              customInput={
                <ForwardedTextInput
                  placeholder={formatRangeText(memberSinceRange)}
                  key={`mem-${memberSinceRange[0]?.getTime() ?? 0}-${memberSinceRange[1]?.getTime() ?? 0}`}
                  InputProps={{
                    endAdornment:
                      memberSinceRange[0] || memberSinceRange[1] ? (
                        <img
                          onClick={(e) => {
                            e.stopPropagation();
                            setMemberSinceRange([null, null]);
                          }}
                          src={CLEAR_X_DATA_URI}
                          alt="Clear"
                          style={{ height: 18, width: 18, cursor: "pointer", marginRight: 10, paddingTop: "4px" }}
                        />
                      ) : undefined,
                  }}
                />
              }
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 2.5, borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 1.25 }}>
        <Button onClick={clearAll} sx={{ border: "1px solid #E7E8E9", color: "#000", borderRadius: 2, height: 40 }}>
          CLEAR ALL
        </Button>
        <Button onClick={handleApply} sx={{ border: "1px solid #C8172F", color: "#C8172F", borderRadius: 2, height: 40 }}>
          APPLY
        </Button>
        <Button onClick={() => onCancel?.()} sx={{ borderRadius: 2, height: 40, display: "none" }}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
}
